import React, { useMemo } from 'react';
import { Pressable, StyleSheet, View, Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import useResponsiveness from '@/hooks/useResponsiveness';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import TaskTemplateFavoriteHeart from '@/components/Tasks/TaskTemplateFavoriteHeart';
import { paletteForSlug, scheduleLabel, isNewTemplate } from '@/utils/tasks_catalog';

const createStyles = (colors, isXS, isAltBGColor) => StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 12,
    paddingHorizontal: 14,
    borderRadius: 12,
    backgroundColor: isAltBGColor ? colors.solid_color_tertiary : colors.solid_color_block,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    width: '100%',
    ...(Platform.OS === 'web' ? { cursor: 'pointer', transition: 'all 0.15s ease' } : {}),
    overflow: 'hidden',
  },
  rowHovered: {
    backgroundColor: isAltBGColor ? colors.solid_color_secondary_dark : colors.solid_color_tertiary,
    borderColor: colors.border_color_primary,
  },
  iconBox: {
    width: isXS ? 40 : 44,
    height: isXS ? 40 : 44,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
  },
  middle: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 6,
  },
  newBadge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    backgroundColor: colors.accent_color_primary,
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 4,
  },
  metaItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  trailing: {
    flexDirection: 'row',
    alignItems: 'center',
    flexShrink: 0,
    gap: 6,
  },
  scheduleBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 8,
    backgroundColor: colors.solid_color_secondary,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
  },
});

function TemplatePinListItem({ template, onPress, onSchedulePress }) {
  const { colors, theme } = useTheme();
  const { t } = useTranslation();
  const { isXS } = useResponsiveness();
  const isAltBGColor = ['sepia', 'muted', 'night', 'smoke'].includes(theme);
  const slug = template?.category?.slug || template?.category;
  const palette = useMemo(() => paletteForSlug(slug), [slug]);
  const isNew = isNewTemplate(template);
  const scheduleHandler = onSchedulePress ?? onPress;

  const styles = useMemo(
    () => createStyles(colors, isXS, isAltBGColor),
    [colors, isXS, isAltBGColor],
  );

  return (
    <Pressable
      onPress={() => onPress?.(template)}
      accessibilityRole="link"
      accessibilityLabel={template?.name}
      style={({ hovered }) => [
        styles.row,
        hovered && styles.rowHovered,
      ]}
    >
      <View style={[styles.iconBox, { backgroundColor: palette.bg }]}>
        <Icon name={template?.icon || 'sparkles'} size={isXS ? 20 : 22} color={palette.fg} />
      </View>

      <View style={styles.middle}>
        <View style={styles.titleRow}>
          <Text weight="bold" size="n" color="primary" numberOfLines={1} style={{ flexShrink: 1 }}>
            {template?.name}
          </Text>
          {isNew && (
            <View style={styles.newBadge}>
              <Text size="xxs" weight="bold" style={{ color: '#fff' }}>
                {t('pages.tasks.newBadge') || 'New'}
              </Text>
            </View>
          )}
        </View>
        <Text size="s" color="secondary" numberOfLines={2}>
          {template?.description}
        </Text>
        <View style={styles.metaRow}>
          <View style={styles.metaItem}>
            <Icon name="arrows-rotate" size={11} color={colors.text_color_tertiary} />
            <Text size="xs" color="tertiary" numberOfLines={1}>
              {scheduleLabel(template)}
            </Text>
          </View>
        </View>
      </View>

      <View style={styles.trailing}>
        <TaskTemplateFavoriteHeart template={template} />
        <Pressable
          onPress={(e) => {
            e?.stopPropagation?.();
            if (typeof e?.preventDefault === 'function') e.preventDefault();
            scheduleHandler?.(template);
          }}
          accessibilityRole="button"
          accessibilityLabel={t('pages.tasks.scheduleIt') || 'Schedule it'}
          style={({ hovered, pressed }) => [
            styles.scheduleBtn,
            hovered && {
              backgroundColor: colors.solid_color_tertiary,
              borderColor: colors.border_color_primary,
            },
            pressed && { opacity: 0.85 },
          ]}
        >
          <Text size="xs" weight="bold" color="secondary">
            {t('pages.tasks.scheduleIt') || 'Schedule it'}
          </Text>
          <Icon name="arrow-right" size={10} color={colors.text_color_secondary} />
        </Pressable>
        <Icon name="chevron-right" size={14} color={colors.text_color_secondary} />
      </View>
    </Pressable>
  );
}

export function TemplatePinListItemPlaceholder() {
  const { colors, theme } = useTheme();
  const { isXS } = useResponsiveness();
  const isAltBGColor = ['sepia', 'muted', 'night', 'smoke'].includes(theme);
  const styles = useMemo(
    () => createStyles(colors, isXS, isAltBGColor),
    [colors, isXS, isAltBGColor],
  );

  return (
    <View style={styles.row}>
      <View style={[styles.iconBox, { backgroundColor: colors.solid_color_secondary, opacity: 0.45 }]} />
      <View style={styles.middle}>
        <View style={{ height: 15, width: '45%', backgroundColor: colors.solid_color_secondary, borderRadius: 4, marginBottom: 8 }} />
        <View style={{ height: 12, width: '92%', backgroundColor: colors.solid_color_secondary, borderRadius: 4, marginBottom: 4 }} />
        <View style={{ height: 12, width: '55%', backgroundColor: colors.solid_color_secondary, borderRadius: 4 }} />
      </View>
    </View>
  );
}

export default React.memo(TemplatePinListItem);
